import type { Moment, PieceData, SectionSpec, WhatIf } from '../types';
import type { SonataFile } from './types';
import { decodeHarmony, decodeMeasures, decodeNotes } from '../chorales/codec';
import {
  CADENCE_MARK,
  CADENCE_NAMES,
  CADENCE_PATH,
  Cadence,
  CadenceKind,
  classifyCadence,
  eventIndexAt,
  pathMatches,
  regionsOf,
  scriptOf,
  soundingAtTime,
  validHarmony,
} from '../chorales/derive';
import { Key, keyName, keyRelation, parseKey, sameKey } from '../../music/key';
import { barAt, buildHarmonyEvents, PunctuationSpec } from '../../music/piece';

/**
 * Sonata movements, derived by rule like the chorales. Form sections come from the analysis
 * ("Form: second theme"); line endings from the DCML phrase annotations, classified by the same
 * cadence rules as the chorales' fermatas; key regions and paths from the harmony. Each form
 * section becomes a moment with words generated from what the rules find in it.
 */

export interface DerivedSonata {
  piece: PieceData;
  cadences: Cadence[];
  matches: Array<{ path: string; start: number; end: number; key: Key }>;
}

const ROMAN = ['', 'I', 'II', 'III', 'IV'];

/**
 * A playing speed for listening, in quarter notes per minute, from the tempo word and the metre.
 * A rule of thumb (the page's speed slider scales it).
 */
export function naturalTempo(tempo: string, time: [number, number]): number {
  const t = tempo.toLowerCase();
  let beat = 120; // Allegro, per main beat
  if (/adagio|largo|grave/.test(t)) beat = 52;
  else if (/andantino/.test(t)) beat = 72;
  else if (/andante/.test(t)) beat = 66;
  else if (/allegretto|moderato/.test(t)) beat = 100;
  else if (/presto/.test(t)) beat = 150;
  if (/allegro/.test(t) && /assai|molto/.test(t)) beat = 138;
  const [num, den] = time;
  const compound = den === 8 && num % 3 === 0;
  if (compound) return Math.round(beat * 1.5 * (beat > 100 ? 0.62 : 0.85)); // dotted-quarter beats
  if (den === 2) return Math.round(beat * 1.25); // cut time: half-note beats, felt faster
  return beat;
}

const RELATION_WORDS: Record<string, string> = {
  V: 'the bright side, a fifth up',
  III: 'the relative major, into the light',
  v: 'the minor dominant',
  vi: 'the relative minor, in shadow',
  IV: 'the soft side, a fifth down',
  iv: 'the soft side, a fifth down',
  ii: 'a minor key a step up',
  i: 'home',
  I: 'home',
};

export type SectionRole =
  | 'first'
  | 'transition'
  | 'second'
  | 'closing'
  | 'development'
  | 'false'
  | 'recap'
  | 'retransition'
  | 'coda'
  | 'theme'
  | 'variation'
  | 'refrain'
  | 'episode'
  | 'minuet'
  | 'trio'
  | 'second-half'
  | 'reprise'
  | 'other';

/**
 * The role of a section from its label. Labels can combine a sonata role and a rondo letter
 * ("rondo A: exposition"); the sonata role wins and the refrain is remembered.
 */
export function roleOf(label: string): { role: SectionRole; letter?: string; refrain?: boolean; abbreviated?: boolean } {
  const l = label.toLowerCase().trim();
  const rondo = /(?:rondo|rounded binary)\s+([a-z1-9])('*)/.exec(l) ?? /^([a-z])('*)(?:$|[:\s(])/.exec(l);
  const letter = rondo ? rondo[1].toUpperCase().replace('1', 'A').replace('2', 'B') + rondo[2] : undefined;
  const refrain = letter?.startsWith('A') ?? false;
  const extra = { ...(letter ? { letter } : {}), ...(refrain ? { refrain } : {}), ...(/abbreviated/.test(l) ? { abbreviated: true } : {}) };
  if (/recapitulation/.test(l)) return { role: 'recap', ...extra };
  if (/false recap/.test(l)) return { role: 'false', ...extra };
  if (/^development|: development|development:/.test(l)) return { role: 'development', ...extra };
  if (/retransition/.test(l)) return { role: 'retransition', ...extra };
  if (/^transition/.test(l)) return { role: 'transition', ...extra };
  if (/closing/.test(l)) return { role: 'closing', ...extra };
  if (/coda/.test(l)) return { role: 'coda', ...extra };
  if (/second (theme|sheme)|secondary/.test(l)) return { role: 'second', ...extra };
  if (/exposition|first theme|primary/.test(l)) return { role: 'first', ...extra };
  if (/^theme/.test(l)) return { role: 'theme' };
  if (/^variation/.test(l)) return { role: 'variation' };
  if (/second section/.test(l)) return { role: 'second-half', ...extra };
  if (/da capo|without repeats|reprise/.test(l)) return { role: 'reprise', ...extra };
  if (/trio|minuet 2|menuetto ii/.test(l)) return { role: 'trio' };
  if (/minuet|menuet/.test(l)) return { role: 'minuet' };
  if (letter) return { role: refrain ? 'refrain' : 'episode', ...extra };
  return { role: 'other' };
}

/**
 * Phrase endings found from the harmony alone, for music whose analysis marks none (the preludes):
 * a root-position dominant (V or V7) resolving to a root-position tonic in the same key, on a
 * strong beat (a bar line or the middle of the bar), at least two bars after the last one.
 * Returns [bar, beat offset in quarters], like the DCML phrase endings.
 */
/**
 * Phrase endings of a piece read entirely by rule (no analyst's phrases): a cadential progression,
 * not just any dominant–tonic. An authentic cadence is a root-position V(7) resolving to a
 * root-position tonic, approached from a predominant (II, IV, an applied chord of V, a Neapolitan
 * or augmented sixth, or a cadential 6-4); a half cadence is a root-position V reached from a
 * predominant on a downbeat and held for a bar. Endings at least two bars apart.
 */
export function cadentialEnds(events: ReturnType<typeof buildHarmonyEvents>, measures: ReturnType<typeof decodeMeasures>): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  const barOf = new Map(measures.map((m) => [m.bar, m]));
  let last = -Infinity;
  const isV = (rn: string) => /^V7?$/.test(rn);
  const isDominantFamily = (rn: string) => /^V(7|65|43|42|6|64)?$/.test(rn) || /^viio?7?$/.test(rn) || rn === 'Cad64';
  const isPredominant = (ev: (typeof events)[number]) => {
    const rn = ev.spec.rn;
    if (/\/V$/.test(rn) || /^(N|It|Fr|Ger)/.test(rn) || rn === 'Cad64' || /^(I|i)64$/.test(rn)) return true;
    return /^(ii|iv|IV|II|bII)/.test(rn) || /^iio|^iiø/.test(rn);
  };
  const before = (i: number) => {
    let j = i - 1;
    while (j >= 0 && isDominantFamily(events[j].spec.rn) && sameKey(events[j].key, events[i].key)) j--;
    return j >= 0 ? events[j] : null;
  };
  for (let i = 1; i < events.length; i++) {
    const ev = events[i];
    const m = barOf.get(ev.bar);
    if (!m) continue;
    const off = ev.start - m.start;
    const prev = events[i - 1];
    let at: number | null = null;
    if (/^(I|i)$/.test(ev.spec.rn) && isV(prev.spec.rn) && sameKey(ev.key, prev.key)) {
      const pd = before(i - 1);
      if (pd && isPredominant(pd)) at = ev.start;
    }
    if (at === null && isV(ev.spec.rn) && Math.abs(off) < 1e-6 && ev.end - ev.start >= m.length - 1e-6) {
      const pd = before(i);
      if (pd && isPredominant(pd)) at = ev.start;
    }
    if (at === null || at - last < 2 * m.length - 1e-6) continue;
    out.push([ev.bar, Math.round(off * 1e4) / 1e4]);
    last = at;
  }
  return out;
}

export function harmonicCadences(events: ReturnType<typeof buildHarmonyEvents>, measures: ReturnType<typeof decodeMeasures>): Array<[number, number]> {
  const out: Array<[number, number]> = [];
  let last = -Infinity;
  const barOf = new Map(measures.map((m) => [m.bar, m]));
  for (let i = 1; i < events.length; i++) {
    const ev = events[i];
    const prev = events[i - 1];
    if (!/^(I|i)$/.test(ev.spec.rn) || !/^V7?$/.test(prev.spec.rn) || !sameKey(ev.key, prev.key)) continue;
    const m = barOf.get(ev.bar);
    if (!m) continue;
    const off = ev.start - m.start;
    const strong = Math.abs(off) < 1e-6 || Math.abs(off - m.length / 2) < 1e-6;
    if (!strong || ev.start - last < 2 * m.length - 1e-6) continue;
    out.push([ev.bar, Math.round(off * 1e4) / 1e4]);
    last = ev.start;
  }
  return out;
}

/**
 * Where a prelude's phrases end: its cadences (above), and, in the long stretches of figuration
 * between them, the last chord before each change of key, so that no passage runs on for pages.
 * Endings closer than three bars to another are dropped.
 */
export function preludeEnds(events: ReturnType<typeof buildHarmonyEvents>, measures: ReturnType<typeof decodeMeasures>, regions: Array<{ from: number; start?: number }>): Array<[number, number]> {
  const barLen = measures.length ? measures[Math.min(1, measures.length - 1)].length : 4;
  const start = new Map(measures.map((m) => [m.bar, m.start]));
  const cad = harmonicCadences(events, measures).map(([bar, off]) => start.get(bar)! + off);
  const turns: number[] = [];
  for (const r of regions.slice(1)) {
    const t = r.start ?? start.get(r.from) ?? 0;
    const before = [...events].reverse().find((e) => e.start < t - 1e-6);
    if (before) turns.push(before.start);
  }
  const kept: number[] = [...cad];
  for (const t of turns.sort((x, y) => x - y)) if (kept.every((k) => Math.abs(k - t) >= 3 * barLen - 1e-6)) kept.push(t);
  return kept
    .sort((x, y) => x - y)
    .map((t) => {
      const m = [...measures].reverse().find((mm) => mm.start <= t + 1e-6)!;
      return [m.bar, Math.round((t - m.start) * 1e4) / 1e4] as [number, number];
    });
}

export function deriveSonata(f: SonataFile): DerivedSonata {
  const measures = decodeMeasures(f);
  const notes = decodeNotes(f);
  const valid = validHarmony(decodeHarmony(f), measures);
  // Home is the key a movement starts in: a minuet whose trio is written last, or a rondo that
  // ends in the major (alla Turca), still has its home where it began.
  const homeKeyToken = valid.length ? valid[0].key : 'C';
  const home = parseKey(homeKeyToken);
  const core = { homeKey: homeKeyToken, measures, notes, harmony: valid, pedals: [] };
  const events = buildHarmonyEvents(core);
  const end = measures[measures.length - 1].start + measures[measures.length - 1].length;
  const starts = new Map(measures.map((m) => [m.bar, m.start]));
  const regions = regionsOf(events, measures, home);

  // Line endings: the DCML phrase ends (plus the final chord), classified like chorale fermatas.
  const topAt = (t: number) => soundingAtTime(notes, t).sort((a, b) => b.midi - a.midi)[0];
  // The final chord ends the last line; if it is repeated (I, then I again), it ends where it arrives.
  let last = events.length - 1;
  while (last > 0 && events[last - 1].spec.rn === events[last].spec.rn && sameKey(events[last - 1].key, events[last].key)) last--;
  const ends = [
    ...new Set([
      ...(f.analysis === 'RULE' ? cadentialEnds(events, measures) : f.kind === 'prelude' ? preludeEnds(events, measures, regions) : f.phraseEnds).filter(([bar]) => starts.has(bar)).map(([bar, beat]) => starts.get(bar)! + beat),
      events.length ? events[last].start : 0,
    ]),
  ].sort((a, b) => a - b);
  // For the preludes: which endings are true cadences (the others cut a passage where the key changes).
  const ruleCadences = new Set(
    f.kind === 'prelude' ? (f.analysis === 'RULE' ? cadentialEnds(events, measures) : harmonicCadences(events, measures)).map(([bar, beat]) => Math.round(((starts.get(bar) ?? 0) + beat) * 1e4) / 1e4) : [],
  );
  const cadences: Cadence[] = ends.map((at) => {
    const i = eventIndexAt(events, at);
    const ev = events[i];
    const kind = classifyCadence(ev, events[i - 1], topAt(at));
    return { at, bar: barAt(measures, at), dur: Math.max(0.5, Math.min(ev.end, end) - at), kind, key: ev.key, event: ev, prev: events[i - 1] };
  });
  // At home: in the home key, or on the home keynote's chord where the analysis keeps the other mode's label (a
  // major final chord in a passage labelled minor).
  const atHome = (cd: Cadence) =>
    sameKey(cd.key, home) || (cd.key.tonic.letter === home.tonic.letter && cd.key.tonic.alter === home.tonic.alter && cd.event.chord.root.letter === home.tonic.letter && cd.event.chord.root.alter === home.tonic.alter);
  const punctuation: PunctuationSpec[] = cadences.map((cd) => {
    const inHome = atHome(cd);
    const plain: Record<CadenceKind, string> = {
      pac: inHome ? 'home' : 'a new home',
      iac: inHome ? 'home, softly' : 'arrival',
      plagal: 'amen',
      tonic: 'a quiet close',
      deceptive: 'the ground gives way',
      half: 'a question',
      phrygian: 'a question',
      pause: 'a pause',
    };
    return { bar: cd.bar, at: cd.at, mark: CADENCE_MARK[cd.kind], label: plain[cd.kind], theory: `${CADENCE_NAMES[cd.kind]}${inHome ? '' : ` in ${keyName(cd.key)}`}.` };
  });

  const matches = pathMatches(events);
  const script = scriptOf(matches, measures);

  // Form sections from the analysis.
  const sections: SectionSpec[] = f.sections
    .filter(([bar]) => starts.has(bar))
    .map(([bar, label, note], i, all) => {
      const start = starts.get(bar)!;
      const next = all[i + 1];
      const stop = next && starts.has(next[0]) ? starts.get(next[0])! : end;
      return { label, start, end: stop, from: bar, to: barAt(measures, stop - 1e-6), ...(note ? { note } : {}) };
    });
  if (sections.length && sections[0].start > measures[0].start + 1e-6) {
    // Music before the first label (an introduction) belongs to the first section.
    sections[0] = { ...sections[0], start: measures[0].start, from: measures[0].bar };
  }

  const keysIn = (a: number, b: number): Key[] => {
    const out: Key[] = [];
    for (const ev of events) {
      if (ev.end <= a + 1e-6 || ev.start >= b - 1e-6) continue;
      if (!out.some((k) => sameKey(k, ev.key))) out.push(ev.key);
    }
    return out;
  };
  const describe = (k: Key) => {
    if (sameKey(k, home)) return `${keyName(k)}, the home key`;
    if (k.tonic.letter === home.tonic.letter && k.tonic.alter === home.tonic.alter) return `${keyName(k)} (the home keynote, ${k.mode === 'minor' ? 'darkened' : 'brightened'})`;
    const rel = keyRelation(home, k);
    return `${keyName(k)} (${RELATION_WORDS[rel] ?? `${rel} from home`})`;
  };

  let afterRecap = false;
  let variation = 0;
  const themeMode = (sections.find((s) => roleOf(s.label).role === 'theme') && keysIn(sections[0].start, sections[0].end)[0]?.mode) || home.mode;
  const moments: Moment[] = sections.map((sec, idx) => {
    const info = roleOf(sec.label);
    const kind = info.role;
    const base = (l?: string) => l?.replace(/'+$/, '');
    const seenBefore = !!info.letter && sections.slice(0, idx).some((x) => base(roleOf(x.label).letter) === base(info.letter));
    if (kind === 'recap') afterRecap = true;
    const keys = keysIn(sec.start, sec.end);
    const first = keys[0] ?? home;
    const main = [...keys].sort((a, b) => {
      const dur = (k: Key) => events.filter((e) => sameKey(e.key, k) && e.start < sec.end && e.end > sec.start).reduce((s, e) => s + Math.min(e.end, sec.end) - Math.max(e.start, sec.start), 0);
      return dur(b) - dur(a);
    })[0] ?? home;
    const cads = cadences.filter((c) => c.at >= sec.start - 1e-6 && c.at < sec.end - 1e-6);
    let feeling = sec.label;
    let listenFor = '';
    switch (kind) {
      case 'first':
        if (afterRecap) {
          feeling = 'The opening idea, at last';
          listenFor = `The first idea comes back late, after the others, in ${describe(main)}.`;
        } else {
          feeling = 'Setting out from home';
          listenFor = `The opening idea, in ${keyName(home)}. Hold on to it: the movement travels away from it and comes back.${info.refrain ? ' It is also a refrain: it will return more than once.' : ''}`;
        }
        break;
      case 'transition':
        feeling = afterRecap ? 'The bridge, rebuilt' : 'Leaving home';
        listenFor = afterRecap
          ? 'The same bridge as before, bent so that this time it can stay at home.'
          : `The music gathers speed and turns toward ${keyName(keys[keys.length - 1] ?? main)}.`;
        break;
      case 'second':
        feeling = afterRecap ? (sameKey(main, home) ? 'The new idea, brought home' : 'The new idea returns') : 'Somewhere new';
        listenFor = afterRecap
          ? `The second idea comes back in ${describe(main)}.`
          : `A second idea, in ${describe(main)}.`;
        break;
      case 'closing':
        feeling = 'Making it final';
        listenFor = `Cadence after cadence in ${keyName(main)}: the new place is confirmed.`;
        break;
      case 'development':
        feeling = 'Wandering';
        listenFor = keys.length > 1 ? `The music travels: ${keys.map((k) => keyName(k)).join(' → ')}.` : `The music works over its ideas in ${keyName(main)}.`;
        break;
      case 'false':
        feeling = 'A trick';
        listenFor = `Something sounds like the return, in ${keyName(first)}, but it is not the real one yet.`;
        break;
      case 'recap':
        feeling = sameKey(first, home) ? 'Coming home' : 'Coming home through the wrong door';
        if (/second (theme|sheme)/i.test(sec.label)) {
          const opening = sections.slice(idx + 1).find((x) => /first theme/i.test(x.label));
          listenFor = `Home again in ${keyName(first)}, but the second idea comes first${opening ? `; the opening idea waits until bar ${opening.from}` : ''}.`;
        } else {
          listenFor = sameKey(first, home)
            ? `The opening idea returns, in ${keyName(home)}.`
            : `The opening idea returns, but in ${describe(first)}, not in ${keyName(home)}. The real homecoming comes later.`;
        }
        break;
      case 'coda':
        feeling = 'The last word';
        listenFor = `After the return, a few more cadences close the movement in ${keyName(home)}.`;
        break;
      case 'theme':
        feeling = 'The tune';
        listenFor = `A theme in ${keyName(first)}, simple enough to remember. Every variation keeps its path of harmonies.`;
        break;
      case 'variation': {
        variation++;
        const minor = first.mode !== themeMode;
        feeling = minor ? 'The same path, in shadow' : `The same path, a new surface`;
        listenFor = minor
          ? `Variation ${variation} turns the theme to ${keyName(first)}: the same harmonic path in the other mode.`
          : `Variation ${variation}: the theme's harmony, dressed in new figures.`;
        break;
      }
      case 'retransition':
        feeling = 'The way back';
        listenFor = `The music prepares a return: listen for the dominant of ${keyName(keys[keys.length - 1] ?? home)} waiting on the doorstep.`;
        break;
      case 'refrain':
        if (!seenBefore) {
          feeling = 'The refrain';
          listenFor = `A tune in ${keyName(main)} that will keep coming back. Remember how it begins.`;
        } else {
          feeling = sameKey(main, home) ? 'The refrain returns' : 'The refrain, somewhere else';
          listenFor = `The tune from the start again${info.abbreviated ? ', shortened' : ''}, in ${describe(main)}.`;
        }
        break;
      case 'episode':
        feeling = seenBefore ? 'The excursion again' : 'Somewhere else';
        listenFor = seenBefore
          ? `The ${info.letter} section again, now in ${describe(main)}.`
          : `Something new${info.letter ? ` (the ${info.letter} section)` : ''}, in ${describe(main)}. The first tune will come back.`;
        break;
      case 'minuet':
        feeling = 'The dance';
        listenFor = `A minuet in ${keyName(main)}, in two halves: the first moves away, the second finds its way back.`;
        break;
      case 'trio':
        feeling = 'The middle dance';
        listenFor = `A contrasting dance, starting in ${describe(first)}, before the first one returns.`;
        break;
      case 'second-half':
        feeling = 'Second half';
        listenFor = keys.length > 1 ? `The second half travels (${keys.map((k) => keyName(k)).join(' → ')}) and finds its way back.` : `The second half of the dance, in ${keyName(main)}.`;
        break;
      case 'reprise':
        feeling = 'The first part again';
        listenFor = `The opening part returns${/without repeats/i.test(sec.label) ? ', this time without its repeats' : ''}, in ${keyName(main)}.`;
        break;
      default:
        listenFor = sec.note || `Keys: ${keys.map((k) => keyName(k)).join(', ')}.`;
    }
    const pathIds = new Set<string>();
    for (const c of cads) {
      const p = CADENCE_PATH[c.kind];
      if (p) pathIds.add(p);
    }
    script.filter((s) => (s.start ?? 0) >= sec.start - 1e-6 && (s.end ?? 0) <= sec.end + 1e-6).forEach((s) => pathIds.add(s.path));
    const counts = new Map<CadenceKind, number>();
    cads.forEach((c) => counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1));
    const cadText = [...counts.entries()].map(([k, n]) => `${n} × ${CADENCE_NAMES[k].toLowerCase()}`).join(', ');
    const feelings =
      kind === 'development' || kind === 'episode' || (kind === 'second' && !afterRecap) ? ['tilt'] : kind === 'recap' || kind === 'coda' || (kind === 'refrain' && seenBefore) ? ['home'] : kind === 'retransition' ? ['waiting'] : [];
    const whatIfs: WhatIf[] = [];
    return {
      id: `section-${idx + 1}`,
      from: sec.from,
      to: sec.to,
      start: sec.start,
      end: sec.end,
      feeling,
      listenFor,
      theoryTitle: `${sec.label[0].toUpperCase()}${sec.label.slice(1)}`,
      theory:
        (idx === 0 && f.formNote ? `The analyst describes the form: “${f.formNote}” ` : '') +
        `Labelled “${sec.label}” in the analysis${f.analyst ? ` by ${f.analyst}` : ''}.` +
        (sec.note ? ` The analyst notes: “${sec.note}”.` : '') +
        ` Keys: ${keys.map((k) => keyName(k)).join(', ') || keyName(home)}.` +
        (cadText ? ` Line endings: ${cadText}.` : ''),
      paths: [...pathIds],
      feelings,
      whatIfs,
    };
  });

  if (!moments.length) {
    // No form labels (a converted analysis): one moment per line, as in the chorales.
    let start = measures[0].start;
    const plainFeeling: Record<CadenceKind, string> = {
      pac: 'Arriving',
      iac: 'Arriving, softly',
      plagal: 'A soft amen',
      tonic: 'Settling',
      deceptive: 'The ground gives way',
      half: 'A question left open',
      phrygian: 'A question, sinking',
      pause: 'A breath',
    };
    cadences.forEach((cd, i) => {
      const stop = Math.min(end, cd.at + cd.dur);
      const path = CADENCE_PATH[cd.kind];
      moments.push({
        id: `line-${i + 1}`,
        from: barAt(measures, start),
        to: cd.bar,
        start,
        end: stop,
        feeling: plainFeeling[cd.kind],
        listenFor: `The phrase ends ${atHome(cd) ? 'at home' : `in ${keyName(cd.key)}`}.`,
        theoryTitle: `Line ${i + 1}: ${CADENCE_NAMES[cd.kind].toLowerCase()}`,
        theory:
          f.kind === 'prelude'
            ? ruleCadences.has(Math.round(cd.at * 1e4) / 1e4)
              ? f.analysis === 'RULE'
                ? 'Phrase ending found by rule from a chord reading that is itself made by rule: a predominant, then a root-position dominant that resolves home (or holds for a bar, a half cadence).'
                : 'Phrase ending found from the harmony by rule (a dominant resolving to the tonic on a strong beat), then classified like the chorale cadences.'
              : i === cadences.length - 1
                ? 'The last chord of the prelude ends the last line.'
                : 'No cadence closes this passage: the rules cut it just before the music changes key, so each stretch of figuration can be heard on its own.'
            : `Line ending from the DCML phrase annotations, classified by rule. This movement's analysis has no form labels.`,
        paths: path ? [path] : [],
        feelings: [],
        whatIfs: [],
      });
      start = stop;
    });
  }

  if (f.kind === 'prelude') {
    return { piece: preludePiece(f, { homeKeyToken, home, measures, notes, valid, regions, punctuation, script, moments }), cadences, matches };
  }
  if (f.kind === 'variations') {
    return { piece: variationPiece(f, { homeKeyToken, home, measures, notes, valid, regions, punctuation, script, moments, sections, end }), cadences, matches };
  }
  const title = `Piano Sonata in ${keyName(f.sonataKey ? parseKey(f.sonataKey) : home)}, K. ${f.k}`;
  // The movement number is in the catalogue ("K. 545 · III"); the subtitle carries tempo and key.
  const mvt = [f.tempo, f.movement > 1 || !f.tempo ? keyName(home) : ''].filter(Boolean).join(', ');
  const piece: PieceData = {
    id: f.id,
    kind: 'sonata',
    title,
    catalogue: `K. ${f.k} · ${ROMAN[f.movement] ?? f.movement}`,
    collection: 'Piano sonata',
    composer: f.composer,
    subtitle: mvt,
    homeKey: homeKeyToken,
    tempo: naturalTempo(f.tempo, f.time),
    measures,
    notes,
    harmony: valid,
    pedals: [],
    regions,
    punctuation,
    script,
    moments,
    sections,
    sources: [
      {
        label: 'Score: DCML Mozart piano sonatas (Hentschel, Neuwirth, Rohrmeier), via When in Rome',
        url: 'https://github.com/DCMLab/mozart_piano_sonatas',
        licence: 'CC BY-NC-SA 4.0 (non-commercial)',
      },
      f.analysis === 'DT'
        ? { label: `Harmonic and form analysis: ${f.analyst}${f.proofreader ? `, proofread by ${f.proofreader}` : ''} (When in Rome)`, url: 'https://github.com/MarkGotham/When-in-Rome', licence: 'CC BY-SA 4.0' }
        : { label: 'Harmonic analysis: DCML (converted by When in Rome)', url: 'https://github.com/DCMLab/mozart_piano_sonatas', licence: 'CC BY-NC-SA 4.0 (non-commercial)' },
      { label: 'Phrase endings: DCML annotations', url: 'https://github.com/DCMLab/mozart_piano_sonatas', licence: 'CC BY-NC-SA 4.0 (non-commercial)' },
    ],
    editorial:
      'Form sections come from the analysis; line endings from the DCML phrase annotations, classified by the same cadence rules as the chorales; key regions, paths and the words on this page are derived by rule.' +
      (f.droppedEndings.length ? ' Repeats are not taken and first-time endings are left out.' : ' Repeats are not taken.'),
  };
  return { piece, cadences, matches };
}

/** Speeds between these are heard as the same tempo (the playback marks wander a little). */
const SAME_SPEED = 0.2;

/**
 * A theme-and-variations set (TAVERN). Sections are the theme and each variation; line endings
 * are the ends of TAVERN's phrases; the speed of each variation comes from the edition's
 * playback marks when it has them, relative to the theme's.
 */
function variationPiece(
  f: SonataFile,
  d: {
    homeKeyToken: string;
    home: Key;
    measures: PieceData['measures'];
    notes: PieceData['notes'];
    valid: PieceData['harmony'];
    regions: PieceData['regions'];
    punctuation: PieceData['punctuation'];
    script: PieceData['script'];
    moments: Moment[];
    sections: SectionSpec[];
    end: number;
  },
): PieceData {
  const set = f.set ?? { title: f.id, catalogue: f.id, tune: '' };
  const starts = new Map(d.measures.map((m) => [m.bar, m.start]));
  const tempi = (f.tempi ?? []).filter(([bar]) => starts.has(bar));
  const base = tempi.length ? tempi[0][1] : naturalTempo(f.tempo || 'allegretto', f.time);
  const tempoMap: Array<{ at: number; factor: number }> = [];
  for (const [bar, bpm] of tempi) {
    const r = bpm / base;
    const factor = Math.abs(Math.log(r)) < SAME_SPEED ? 1 : Math.round(Math.max(0.4, Math.min(2.2, r)) * 20) / 20;
    const last = tempoMap.length ? tempoMap[tempoMap.length - 1].factor : 1;
    if (factor !== last) tempoMap.push({ at: starts.get(bar)!, factor });
  }
  // The marks are the edition's playback speeds; keep them within a range that suits listening.
  const tempo = Math.max(48, Math.min(144, Math.round(base)));
  const factorAt = (t: number) => {
    let f = 1;
    for (const x of tempoMap) if (x.at <= t + 1e-6) f = x.factor;
    return f;
  };
  const speedWords = (f: number) =>
    f <= 0.6 ? ' It moves at about half the speed of the theme.' : f < 0.9 ? ' It moves more slowly than the theme.' : f >= 1.6 ? ' It moves much faster than the theme.' : f > 1.1 ? ' It moves faster than the theme.' : '';
  const moments = d.moments.map((m) => (m.start !== undefined && /^variation|^coda/i.test(m.theoryTitle) ? { ...m, listenFor: m.listenFor + speedWords(factorAt(m.start)) } : m));
  const n = d.sections.filter((s) => /^variation/i.test(s.label)).length;
  return {
    id: f.id,
    kind: 'variations',
    title: set.title,
    catalogue: set.catalogue,
    collection: 'Theme and variations',
    composer: f.composer,
    subtitle: `${keyName(d.home)}, a theme and ${n} variations`,
    ...(set.tune ? { tune: set.tune } : {}),
    homeKey: d.homeKeyToken,
    tempo,
    ...(tempoMap.length ? { tempoMap } : {}),
    measures: d.measures,
    notes: d.notes,
    harmony: d.valid,
    pedals: [],
    regions: d.regions,
    punctuation: d.punctuation,
    script: d.script,
    moments,
    sections: d.sections,
    sources: [
      {
        label: 'Score, phrases and harmonic analysis: TAVERN, Theme And Variation Encodings with Roman Numerals (Devaney, Arthur, Condit-Schultz, Nisula 2015), annotator A',
        url: 'https://github.com/jcdevaney/TAVERN',
        licence: 'CC BY-SA 4.0',
      },
    ],
    editorial:
      (set.tune ? `${set.tune} ` : '') +
      'The theme and each variation are found by placing TAVERN’s phrases in the score by their notes; line endings are the ends of those phrases, classified by the same cadence rules as the chorales; key regions, paths and the words on this page are derived by rule. Repeats are not taken.',
  };
}

/** A prelude of The Well-Tempered Clavier, Book I, read like the other keyboard music. */
function preludePiece(
  f: SonataFile,
  d: {
    homeKeyToken: string;
    home: Key;
    measures: PieceData['measures'];
    notes: PieceData['notes'];
    valid: PieceData['harmony'];
    regions: PieceData['regions'];
    punctuation: PieceData['punctuation'];
    script: PieceData['script'];
    moments: Moment[];
  },
): PieceData {
  const set = f.set ?? { title: f.id, catalogue: f.id, tune: '' };
  // Plain names for the moments: "Phrase 3" rather than "Line 3", and the key of each ending.
  const moments = d.moments.map((m, i) => ({ ...m, theoryTitle: m.theoryTitle.replace(/^Line /, 'Phrase '), id: `phrase-${i + 1}` }));
  if (set.collection) {
    // Later piano music imported from MIDI: no published analysis, so everything here is read by rule.
    return {
      id: f.id,
      kind: 'prelude',
      title: `${set.title} in ${keyName(d.home)}`,
      catalogue: set.catalogue,
      collection: set.collection,
      composer: f.composer,
      year: set.year || undefined,
      homeKey: d.homeKeyToken,
      tempo: f.bpm ?? naturalTempo('', f.time),
      measures: d.measures,
      notes: d.notes,
      harmony: d.valid,
      pedals: [],
      regions: d.regions,
      punctuation: d.punctuation,
      script: d.script,
      moments,
      sources: [
        { label: `Score: ${set.source ?? 'MIDI file'} (notes and timing; spelling, voices and written values reconstructed by rule)`, url: '', licence: 'not stated' },
        { label: 'Harmonic analysis: by rule in this project (key finding and chord matching); no published analysis of this piece is used', url: '', licence: 'this project' },
      ],
      editorial:
        'This piece came as a MIDI file, which has no spelling, voices or analysis. Keys were found bar by bar from the pitch content, chords matched beat by beat against the chords of the key, and phrase endings found from the harmony, all by rule; treat the reading as a first draft, not an edition. The listening speed is the MIDI file’s.',
    };
  }
  return {
    id: f.id,
    kind: 'prelude',
    title: `Prelude in ${keyName(d.home)}`,
    catalogue: set.catalogue,
    collection: 'The Well-Tempered Clavier, Book I',
    composer: f.composer,
    year: '1722',
    subtitle: set.title,
    homeKey: d.homeKeyToken,
    tempo: f.bpm ?? naturalTempo('', f.time),
    measures: d.measures,
    notes: d.notes,
    harmony: d.valid,
    pedals: [],
    regions: d.regions,
    punctuation: d.punctuation,
    script: d.script,
    moments,
    sources: [
      {
        label: 'Score encoding: When in Rome corpus, The Well-Tempered Clavier I (score.mxl; no licence stated, left out of the commercial build)',
        url: 'https://github.com/MarkGotham/When-in-Rome',
        licence: 'unverified',
      },
      { label: `Harmonic analysis: ${f.analyst || 'Mark Gotham'} and colleagues (When in Rome)`, url: 'https://github.com/MarkGotham/When-in-Rome', licence: 'CC BY-SA 4.0' },
    ],
    editorial:
      'Key regions, phrase endings (found from the harmony by rule), patterns and the words on this page are derived by rule from the analysis. The listening speed is an editorial choice: Bach wrote no tempo marks. Repeats are not taken.',
  };
}
